import "./style.css";

type FieldConfig = {
    type?: "text" | "number" | "boolean" | "multiselect" | "select";
    options?: string[];
    default?: any;
    dynamic?: boolean;
    disabled?: boolean;
    required?: true;
    'form-text'?: string;
};

/* ------------------------------------------------------------------ */
/* validation state */
/* ------------------------------------------------------------------ */

type AnyInput =
    | HTMLInputElement
    | HTMLSelectElement
    | HTMLTextAreaElement;

type TabValidationState = {
    inputs: AnyInput[];
    badge: HTMLElement;
    valid: boolean;
};

const tabValidation = new Map<string, TabValidationState>();

/* ------------------------------------------------------------------ */
/* entry point */
/* ------------------------------------------------------------------ */
// 1. Keep ONE source of truth: the config object
let currentConfig: Record<string, any> = {};

export function makeConfigForms(
    pyaeroptions: Record<string, any>,
    divId: string
) {
    const rootEl = document.getElementById(divId);
    if (!rootEl) return;

    rootEl.innerHTML = "";

    const nav = document.createElement("ul");
    nav.className = "nav nav-tabs";

    const content = document.createElement("div");
    content.className = "tab-content mt-3";

    rootEl.append(nav, content);

    Object.entries(pyaeroptions).forEach(([tabKey, tabConfig], index) => {
        const tabId = `tab-${tabKey}`;

        /* ---------- tab button ---------- */
        const li = document.createElement("li");
        li.className = "nav-item";

        const button = document.createElement("button");
        button.className = `nav-link ${index === 0 ? "active" : ""}`;
        button.dataset.bsToggle = "tab";
        button.dataset.bsTarget = `#${tabId}`;
        button.type = "button";
        button.role = "tab";
        button.textContent = tabKey;

        const badge = document.createElement("span");
        badge.className = "badge badge-pill bg-secondary ms-2";
        badge.textContent = "?";

        button.appendChild(badge);
        li.appendChild(button);
        nav.appendChild(li);

        tabValidation.set(tabKey, {
            inputs: [],
            badge,
            valid: false
        });

        /* ---------- tab panel ---------- */
        const panel = document.createElement("div");
        panel.className = `tab-pane fade ${index === 0 ? "show active" : ""}`;
        panel.id = tabId;

        content.appendChild(panel);

        buildForm(tabConfig, panel, [tabKey], tabKey);
        updateTabStatus(tabKey)
    });
}

/* ------------------------------------------------------------------ */
/* recursive builder */
/* ------------------------------------------------------------------ */

function buildForm(
    obj: Record<string, any>,
    container: HTMLElement,
    path: string[],
    rootTab: string
) {

    // Special handling for variables root: variables is a multiselect with an options object
    if (
        rootTab === "variables" &&
        path.length === 1 &&
        obj &&
        obj.type === "multiselect" &&
        obj.options &&
        typeof obj.options === "object"
    ) {
        for (const [varName, varCfg] of Object.entries(obj.options)) {
            createVariableSection(varName, varCfg, container, rootTab);
        }
        return; // Prevent processing of keys like type/default/options as normal fields
    }

    for (const [key, value] of Object.entries(obj)) {
        if (key === "default" || key === "dynamic" || key === "disabled") continue;

        const currentPath = [...path, key];
        const pathStr = currentPath.join(".");

        // 1. Known field with .type → render as input
        if (value && typeof value === "object" && value.type) {
            if (value.type === "boolean") {
                createBooleanToggle(pathStr, value.default ?? false, container, rootTab, value);
            } else if (value.type === "select") {
                createSelect(pathStr, value, container, rootTab, value);
            } else if (value.type === "multiselect") {
                createMultiSelectCheckboxes(pathStr, container, rootTab, value);
            } else {
                // text, number, textarea, etc.
                createTextInput(pathStr, value.default ?? "", container, rootTab, value);
            }
            continue;
        }

        // 2. Nested group (no .type) → recurse
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
            const fieldset = document.createElement("fieldset");
            fieldset.className = "border rounded p-3 mb-4";
            const legend = document.createElement("legend");
            legend.className = "float-none w-auto px-2";
            legend.textContent = key;
            fieldset.appendChild(legend);
            container.appendChild(fieldset);
            buildForm(value, fieldset, currentPath, rootTab);
            continue;
        }

        // 3. Legacy primitive fallback
        createTextInput(pathStr, value, container, rootTab, { default: value });
    }
}

/* ------------------------------------------------------------------ */
/* variable handling */
/* ------------------------------------------------------------------ */

function createVariableSection(
    variable: string,
    config: any,
    container: HTMLElement,
    tabKey: string
) {
    const fs = document.createElement("fieldset");
    fs.className = "border rounded p-2 mb-2 d-none";
    fs.dataset.variableName = variable;

    const legend = document.createElement("legend");
    legend.className = "float-none w-auto px-2 d-flex gap-2";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.dataset.variableSelector = "true";
    checkbox.value = variable;

    const label = document.createElement("span");
    label.textContent = variable.split('-')[0];

    legend.append(checkbox, label);
    fs.appendChild(legend);
    container.appendChild(fs);

    // Register top-level variable selection
    registerInput(tabKey, checkbox, `variables.${variable}`);

    // Render nested configs (e.g., networks)
    buildForm(
        {
            networks: {
                type: "multiselect",
                options: config.networks?.options ?? [],
                default: config.networks?.default ?? []
            }
        },
        fs,
        [`variables.${variable}`],
        tabKey
    );

    // Store default networks in each checkbox
    fs.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:not([data-variable-selector])')
        .forEach(n => {
            n.dataset.defaultValues = (config.networks?.default ?? []).join(",");
        });

    checkbox.addEventListener("change", () => {
        toggleFieldset(fs, checkbox.checked);
        updateTabStatus(tabKey);
    });

    toggleFieldset(fs, false); // Start disabled until checked
}



/* ------------------------------------------------------------------ */
/* inputs */
/* ------------------------------------------------------------------ */

function createTextInput(
    name: string,
    value: any,
    container: HTMLElement,
    tabKey: string,
    config: FieldConfig
) {
    const div = document.createElement("div");
    div.className = "mb-3";

    const label = document.createElement("label");
    label.className = "form-label";
    label.textContent = name;
    div.appendChild(label);

    const input = document.createElement("input");
    input.type = config.type === "number" ? "number" : "text";
    input.className = "form-control";
    input.value = config.default ?? value ?? "";

    if (config.disabled) {
        input.disabled = true;
        div.classList.add("opacity-75");
    }

    if (config.required) {
        input.required = true;
        const invalidFeedback = document.createElement("div");
        invalidFeedback.className = "invalid-feedback";
        invalidFeedback.textContent = 'input required';
        div.appendChild(invalidFeedback);
    }

    registerInput(tabKey, input, name);
    div.appendChild(input);

    // help text
    if (config["form-text"]) {
        const help = document.createElement("div");
        help.className = "form-text text-muted";
        help.textContent = config["form-text"];
        div.appendChild(help);
    }

    container.appendChild(div);
}

function createSelect(
    name: string,
    cfg: SelectOption,
    container: HTMLElement,
    tabKey: string,
    config: FieldConfig
) {
    const div = document.createElement("div");
    div.className = "mb-4";

    const label = document.createElement("label");
    label.className = "form-label";
    label.textContent = name;

    const select = document.createElement("select");
    select.className = "form-select";
    select.multiple = cfg.type === "multiselect";

    for (const opt of cfg.options) {
        const o = document.createElement("option");
        o.value = opt;
        o.textContent = opt;

        if (
            Array.isArray(cfg.default)
                ? cfg.default.includes(opt)
                : cfg.default === opt
        ) {
            o.selected = true;
        }
        select.appendChild(o);
    }

    registerInput(tabKey, select, name);

    div.append(label, select);

    // help text
    if (config["form-text"]) {
        const help = document.createElement("div");
        help.className = "form-text text-muted";
        help.textContent = config["form-text"];
        div.appendChild(help);
    }
    container.appendChild(div);
}

function createMultiSelectCheckboxes(
    path: string,
    container: HTMLElement,
    tabKey: string,
    config: FieldConfig,
) {
    const div = document.createElement("div");
    div.className = "mb-4";

    const label = document.createElement("div");
    label.className = "form-label fw-semibold mb-1";
    label.textContent = path;

    div.appendChild(label);

    config.options?.forEach(opt => {
        const checkWrap = document.createElement("div");
        checkWrap.className = "form-check";

        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.className = "form-check-input";
        checkbox.value = opt;
        checkbox.checked = Array.isArray(config.default)
            ? config.default.includes(opt)
            : false;
        checkbox.disabled = config.disabled ? true : false

        // store defaults
        checkbox.dataset.defaultValues = (config.default ?? []).join(",");

        checkbox.dataset.multiselectGroup = path;
        checkbox.dataset.option = opt;

        const lbl = document.createElement("label");
        lbl.className = "form-check-label";
        lbl.htmlFor = checkbox.id = `cb-${path.replace(/\./g, "-")}-${opt}`;
        lbl.textContent = opt;

        checkWrap.append(checkbox, lbl);
        div.appendChild(checkWrap);
        registerInput(tabKey, checkbox, `${path}.${opt}`);
    });

    // help text
    if (config["form-text"]) {
        const help = document.createElement("div");
        help.className = "form-text text-muted";
        help.textContent = config["form-text"];
        div.appendChild(help);
    }

    container.appendChild(div);
}


/* ------------------------------------------------------------------ */
/* helpers */
/* ------------------------------------------------------------------ */

function createBooleanToggle(
    name: string,
    checked: boolean,
    container: HTMLElement,
    tabKey: string,
    config: any = {}
) {
    const div = document.createElement("div");
    div.className = "form-check mb-4";

    const input = document.createElement("input");
    input.type = "checkbox";
    input.className = "form-check-input";
    input.checked = checked;
    input.disabled = !!config.disabled;

    const label = document.createElement("label");
    label.className = "form-check-label";
    label.textContent = name;

    registerInput(tabKey, input, name);

    div.append(input, label);

    // help text
    if (config["form-text"]) {
        const help = document.createElement("div");
        help.className = "form-text text-muted";
        help.textContent = config["form-text"];
        div.appendChild(help);
    }

    container.append(div);
}


function toggleFieldset(fs: HTMLFieldSetElement, enabled: boolean) {
    const selector = fs.querySelector<HTMLInputElement>(
        'input[data-variable-selector="true"]'
    );

    // variable selector must always stay enabled
    if (selector) selector.disabled = false;

    for (const el of fs.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
        "input, select"
    )) {
        if (el === selector) continue;
        el.disabled = !enabled;
    }
}

function registerInput(tab: string, input: AnyInput, path?: string) {
    const entry = tabValidation.get(tab);
    if (!entry) return;

    if (path) input.dataset.cfgPath = path;
    entry.inputs.push(input);

    input.addEventListener("change", () => updateTabStatus(tab));
    input.addEventListener("input", () => updateTabStatus(tab));
}

export function updateTabStatus(tab: string) {

    const state = tabValidation.get(tab);
    if (!state) return;

    let touched = false;
    let valid = true;

    for (const i of state.inputs) {
        if (!i.disabled) {
            if (i instanceof HTMLInputElement && i.type === "checkbox") {
                touched ||= i.checked;
            } else if (i.value !== "") {
                touched = true;
            }
        }
        if (!isValidInput(i)) valid = false;
    }

    // reset make-cfg-status message
    const makeCfgStatusElement = document.getElementById('make-cfg-status')
    const btn = document.getElementById("make-cfg-but") as HTMLButtonElement;
    const submitBtn = document.getElementById("submit-but") as HTMLButtonElement;
    if (makeCfgStatusElement) {
        if (makeCfgStatusElement.innerHTML.includes('badge')) {
            makeCfgStatusElement.innerHTML = '<span class="badge badge-pill bg-secondary ms-2 icon-badge">modified</span>'
        }
        btn.disabled = true;
        submitBtn.disabled = true;
    }

    // Special validation rules
    if (tab === "variables") {
        state.valid = hasSelectedVariable();
    } else if (tab === "time") {
        state.valid = hasSelectedPeriod() && hasSelectedFrequency();
    } else {
        state.valid = touched && valid;
    }

    // Badge update
    state.badge.className = state.valid
        ? "badge badge-pill bg-success ms-2 icon-badge"
        : "badge badge-pill bg-danger ms-2 icon-badge";
    state.badge.textContent = state.valid ? "✓" : "⨯";

    // EMPTY MESSAGE LOGIC — moved here
    const container = document.getElementById(`tab-${tab}`);
    const msg = container?.querySelector<HTMLElement>(".empty-message");

    if (tab === "variables" || tab === "time") {
        const hasData =
            (tab === "variables" && currentConfig.variables?.options && Object.keys(currentConfig.variables.options).length > 0) ||
            (tab === "time" && (
                (currentConfig.time?.periods?.options && currentConfig.time.periods.options.length > 0)
            ));

        if (!hasData && container) {
            // Create message if not exists
            let messageEl = container.querySelector<HTMLElement>(".empty-message");
            if (!messageEl) {
                messageEl = document.createElement("div");
                messageEl.className = "alert alert-primary empty-message";
                messageEl.textContent = `No ${tab} available. Please select at least one file.`;
                container.appendChild(messageEl);
            }
        } else if (msg) {
            msg.remove();
        }
    }

    updateValidateButton();
}

function updateValidateButton() {
    const btn = document.getElementById("make-cfg-but") as HTMLButtonElement;
    const submitBtn = document.getElementById("submit-but") as HTMLButtonElement;
    const makeCfgStatus = document.getElementById("make-cfg-status") as HTMLElement;
    const submitStatus = document.getElementById("submit-status") as HTMLElement;

    if (!btn || !submitBtn) return;
    btn.disabled = ![...tabValidation.values()].every(t => t.valid);
    if (makeCfgStatus.innerHTML.includes('modified')) {
        submitBtn.disabled = true;
        submitStatus.textContent = 'valid configuration required'
    }

    const total = document.querySelectorAll('#config-file-form>ul>li.nav-item>button>span').length
    const success = document.querySelectorAll('#config-file-form>ul>li.nav-item>button>span.bg-success').length
    makeCfgStatus.innerHTML = `${success}/${total}`
}

function isValidInput(i: AnyInput) {
    if (i.disabled) return true;
    if (i instanceof HTMLSelectElement && i.multiple) {
        return i.selectedOptions.length > 0;
    }
    if (i instanceof HTMLInputElement && i.type === "checkbox") {
        return true;
    }
    return i.value.trim() !== "";
}

function hasSelectedVariable() {
    const t = tabValidation.get('variables');
    if (!t) return false;

    return t.inputs.some(
        i =>
            i instanceof HTMLInputElement &&
            i.dataset.variableSelector &&
            i.checked
    );
}

function hasSelectedPeriod(): boolean {
    return Array.from(document.querySelectorAll('#tab-time input[data-multiselect-group="time.periods"]'))
        .some((el: any) => el.checked);
}

function hasSelectedFrequency(): boolean {
    return Array.from(document.querySelectorAll('#tab-time input[data-multiselect-group="time.frequency"]'))
        .some((el: any) => el.checked);
}

export function updateVariableTab(availableVariables: Set<string>) {
    const tabKey = "variables";
    const tab = tabValidation.get(tabKey);
    if (!tab) return;

    const container = document.querySelector<HTMLElement>("#tab-variables");
    if (!container) return;

    // === YOUR EXISTING CODE (keep it!) ===
    container.querySelectorAll<HTMLElement>("fieldset[data-variable-name]")
        .forEach(fs => {
            const varName = fs.dataset.variableName!;
            const checkbox = fs.querySelector<HTMLInputElement>(
                'input[data-variable-selector="true"]'
            );

            const available = availableVariables.has(varName);
            fs.classList.toggle("d-none", !available);

            if (checkbox) {
                if (available) {
                    checkbox.checked = true;
                    toggleFieldset(fs as HTMLFieldSetElement, true);

                    fs.querySelectorAll<HTMLInputElement>(
                        'input[type="checkbox"]:not([data-variable-selector])'
                    ).forEach(n => {
                        const defaults = (n.dataset.defaultValues || "").split(",");
                        n.checked = defaults.includes(n.value);
                    });
                } else {
                    checkbox.checked = false;
                    toggleFieldset(fs as HTMLFieldSetElement, false);
                    fs.querySelectorAll<HTMLInputElement>(
                        'input[type="checkbox"]:not([data-variable-selector])'
                    ).forEach(n => n.checked = false);
                }
            }
        });

    // === NEW: Sync currentConfig with visible variables ===
    if (!currentConfig.variables) {
        currentConfig.variables = { type: "multiselect", options: {}, default: [] };
    }

    const newOptions: Record<string, any> = {};
    container.querySelectorAll("fieldset[data-variable-name]:not(.d-none)").forEach(fs => {
        const varName = (fs as any).dataset.variableName!;
        // Preserve existing network config (critical!)
        newOptions[varName] = currentConfig.variables.options?.[varName] || {
            networks: { type: "multiselect", options: [], default: [] }
        };
    });

    currentConfig.variables.options = newOptions;

    // If nothing visible → empty options
    if (availableVariables.size === 0) {
        currentConfig.variables.options = {};
    }

    updateTabStatus(tabKey);
}



/* ------------------------------------------------------------------ */
/* collect */
/* ------------------------------------------------------------------ */

export function collectConfigFromForms(): Record<string, any> {
    const cfg: Record<string, any> = {};
    const selectedVariables = new Set<string>();

    for (const tab of tabValidation.values()) {
        for (const input of tab.inputs) {
            const path = input.dataset.cfgPath;
            if (!path) continue;

            // Skip disabled inputs except variable selectors
            if (input.disabled && input.dataset.variableSelector !== "true") continue;

            // 1. Variable selector (top-level checkbox)
            if ((input as HTMLInputElement).dataset.variableSelector === "true") {
                if ((input as HTMLInputElement).checked) {
                    selectedVariables.add((input as HTMLInputElement).value);
                }
                continue;
            }

            // 2. Multiselect checkboxes (networks, statistics, map_type, etc.)
            if (
                input instanceof HTMLInputElement &&
                input.type === "checkbox" &&
                input.dataset.multiselectGroup &&
                input.dataset.option
            ) {
                appendDeepArray(cfg, input.dataset.multiselectGroup, input.dataset.option, input.checked);
                continue;
            }

            // 3. Boolean toggles
            if (input instanceof HTMLInputElement && input.type === "checkbox") {
                setDeep(cfg, path, input.checked);
                continue;
            }

            // 4. Select (single or multiple)
            if (input instanceof HTMLSelectElement) {
                const value = input.multiple
                    ? Array.from(input.selectedOptions).map(o => o.value)
                    : input.value;
                setDeep(cfg, path, value);
                continue;
            }

            // 5. Number inputs
            if (input instanceof HTMLInputElement && input.type === "number") {
                const num = Number(input.value);
                setDeep(cfg, path, isNaN(num) ? 0 : num);
                continue;
            }

            // 6. Text / fallback
            if ("value" in input && typeof (input as any).value === "string") {
                setDeep(cfg, path, (input as any).value.trim());
            }
        }
    }

    // Finalize variables object
    const variablesFinal: Record<string, string[]> = {};
    for (const varName of selectedVariables) {
        const networks = cfg.variables?.[varName]?.networks ?? [];
        variablesFinal[varName] = Array.isArray(networks) ? networks : [];
    }

    cfg.variables = variablesFinal;

    return cfg;
}



function setDeep(obj: any, path: string, value: any) {
    const keys = path.split(".");
    let cur = obj;

    keys.forEach((k, i) => {
        if (i === keys.length - 1) {
            cur[k] = value;
        } else {
            cur[k] ||= {};
            cur = cur[k];
        }
    });
}

function appendDeepArray(obj: any, path: string, value: string, enabled: boolean) {
    const keys = path.split(".");
    let cur = obj;

    for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (i === keys.length - 1) {
            cur[k] ??= [];
            if (enabled && !cur[k].includes(value)) cur[k].push(value);
            else if (!enabled) cur[k] = cur[k].filter((v: string) => v !== value);
        } else {
            cur[k] ??= {};
            cur = cur[k];
        }
    }
}

export function resetForms() {
    tabValidation.clear();
    document.querySelectorAll("input, select").forEach(el => {
        if (el instanceof HTMLInputElement && el.type === "checkbox") {
            el.checked = el.dataset.defaultValues?.split(",").includes(el.value) || false;
        } else if ("value" in el) {
            (el as any).value = (el as any).defaultValue || "";
        }
    });
}

export function setForm(path: string, value: string | number | boolean | string[]) {
    const elements = document.querySelectorAll<HTMLElement>(
        `[data-cfg-path="${path}"], [data-cfg-path^="${path}."]`
    );

    if (elements.length === 0) {
        console.warn(`setForm: No element found for path "${path}"`);
        return;
    }

    // Handle single-value inputs (text, number, select-one, checkbox boolean)
    if (!Array.isArray(value)) {
        elements.forEach(el => {
            if (el instanceof HTMLInputElement) {
                if (el.type === "checkbox") {
                    el.checked = Boolean(value);
                } else {
                    el.value = String(value);
                }
            } else if (el instanceof HTMLSelectElement) {
                el.value = String(value);
            }

            // Trigger updates
            el.dispatchEvent(new Event("input", { bubbles: true }));
            el.dispatchEvent(new Event("change", { bubbles: true }));
        });
        return;
    }

    // Handle multiselect arrays: ["AERONET L1.5", "MODIS"]
    if (Array.isArray(value)) {
        const valueSet = new Set(value.map(String));

        elements.forEach(el => {
            if (el instanceof HTMLInputElement && el.type === "checkbox") {
                const optionValue = el.dataset.option || el.value;
                el.checked = valueSet.has(optionValue);
            } else if (el instanceof HTMLSelectElement && el.multiple) {
                Array.from(el.options).forEach(opt => {
                    opt.selected = valueSet.has(opt.value);
                });
            }

            el.dispatchEvent(new Event("change", { bubbles: true }));
        });
    }
}


// 2. Render once (or re-render when config changes)
export function renderConfigForms(baseConfig: Record<string, any>) {
    currentConfig = structuredClone(baseConfig);   // deep copy
    makeConfigForms(currentConfig, "config-root");
}

// 3. Helper: mutate config + re-render (or partial re-render)
export function updateConfig(partial: Record<string, any>, tab?: string) {
    // deep merge (simple version – you can use lodash/merge if you want)
    deepMerge(currentConfig, partial);

    if (tab) {
        // cheap: only rebuild one tab
        const container = document.getElementById(`tab-${tab}`);
        if (container) {
            container.innerHTML = "";
            buildForm(currentConfig[tab], container, [tab], tab);
        }
        updateTabStatus(tab)
    } else {
        // full rebuild – still < 10 ms for your size
        makeConfigForms(currentConfig, "config-root");
    }

}

export function deepMerge(target: any, source: any): any {
    if (!source || typeof source !== "object") return target;
    if (!target || typeof target !== "object") return source;

    for (const key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            const src = source[key];
            if (
                src &&
                typeof src === "object" &&
                src.constructor === Object &&           // plain object only
                target[key] &&
                typeof target[key] === "object" &&
                target[key].constructor === Object
            ) {
                deepMerge(target[key], src);
            } else {
                target[key] = src;  // replace arrays, strings, numbers, booleans
            }
        }
    }
    return target;
}
